import { useMemo, useState } from 'react';
import { AlertTriangle, Check, MoreHorizontal, Paperclip } from 'lucide-react';
import {
  buildQuoteComparison,
  describeDepositTerms,
  formatCurrency,
  isOpenQuote,
  QUOTE_STATUS_LABELS,
  QuoteStatus,
  type ComparisonCell,
  type PlanningItem,
  type Quote,
} from '@peles/shared';
import { SegmentedControl } from '../common/SegmentedControl';
import { DropdownMenu, type DropdownMenuItem } from '../common/DropdownMenu';
import { StatusBadge } from '../common/EntityList';
import { itemLabel } from './quoteDraft';
import { shortDay } from './needDisplay';

export interface QuoteComparisonProps {
  quotes: Quote[];
  items: PlanningItem[];
  budget: number | null;
  /** The office's day, `YYYY-MM-DD` — a quote valid until before it has lapsed. */
  today: string;
  /** Booked: the quotes stay for reference, and nothing is offered. */
  readOnly?: boolean;
  onAccept: (quote: Quote) => void;
  onRecord: (quote: Quote) => void;
  onDecline: (quote: Quote) => void;
  onWithdraw: (quote: Quote) => void;
}

const STATUS_COLORS: Record<string, string> = {
  REQUESTED: 'yellow',
  RECEIVED: 'blue',
  ACCEPTED: 'green',
  DECLINED: 'gray',
  WITHDRAWN: 'gray',
};

const money = (n: number) => formatCurrency(n);

function Amount({ cell }: { cell: ComparisonCell | undefined }) {
  if (!cell || cell.amount === null) return <span className="text-gray-400">—</span>;
  return (
    <span className={cell.lowest ? 'font-semibold text-green-700' : 'text-gray-900'}>
      {money(cell.amount)}
      {cell.lowest && <span className="sr-only"> (lowest)</span>}
    </span>
  );
}

function signed(n: number | null): string {
  if (n === null) return '—';
  if (n === 0) return 'on budget';
  return n > 0 ? `+${money(n)}` : `−${money(-n)}`;
}

/**
 * A need's quotes side by side: one row per item, then each vendor's extras,
 * so nothing quoted is hidden; the lowest in each row marked, a missing price
 * "—" and never $0. On a phone, one vendor's card at a time.
 */
export function QuoteComparison({
  quotes,
  items,
  budget,
  today,
  readOnly = false,
  onAccept,
  onRecord,
  onDecline,
  onWithdraw,
}: QuoteComparisonProps) {
  const shown = useMemo(
    () => quotes.filter((q) => isOpenQuote(q.status) || q.status === QuoteStatus.ACCEPTED),
    [quotes],
  );
  const closed = useMemo(
    () => quotes.filter((q) => !isOpenQuote(q.status) && q.status !== QuoteStatus.ACCEPTED),
    [quotes],
  );
  const matrix = useMemo(
    () =>
      buildQuoteComparison(
        items.map((i) => ({
          id: i.id,
          itemName: itemLabel(i),
          quantity: i.quantity,
          unitPrice: i.unitPrice,
        })),
        shown,
        budget,
      ),
    [items, shown, budget],
  );
  const [phoneQuoteId, setPhoneQuoteId] = useState<string | null>(null);
  const phoneQuote = shown.find((q) => q.id === phoneQuoteId) ?? shown[0];
  const lowest = shown.find((q) => q.id === matrix.lowestQuoteId);

  const menuFor = (q: Quote): DropdownMenuItem[] =>
    isOpenQuote(q.status)
      ? [
          { id: 'decline', label: 'Decline', onSelect: () => onDecline(q) },
          { id: 'withdraw', label: 'Vendor dropped out', onSelect: () => onWithdraw(q) },
        ]
      : [];

  const expired = (q: Quote) => !!q.validUntil && isOpenQuote(q.status) && q.validUntil < today;

  const actions = (q: Quote, compact = false) => {
    if (readOnly || !isOpenQuote(q.status)) return null;
    const menu = menuFor(q);
    return (
      <div className={`flex items-center gap-1 ${compact ? '' : 'justify-end'}`}>
        <button
          type="button"
          onClick={() => onRecord(q)}
          className="min-h-11 sm:min-h-8 px-2 text-xs border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
        >
          {q.totalAmount === null ? 'Enter figures' : 'Revise'}
        </button>
        {q.totalAmount !== null && (
          <button
            type="button"
            onClick={() => onAccept(q)}
            aria-label={`Accept ${q.vendorName}`}
            className="min-h-11 sm:min-h-8 px-2 text-xs bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700"
          >
            Accept
          </button>
        )}
        {menu.length > 0 && (
          <DropdownMenu
            align="end"
            ariaLabel={`More for ${q.vendorName}`}
            items={menu}
            trigger={(props) => (
              <button
                type="button"
                {...props}
                aria-label={`More for ${q.vendorName}`}
                className="min-h-11 min-w-11 sm:min-h-8 sm:min-w-8 flex items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100"
              >
                <MoreHorizontal size={16} />
              </button>
            )}
          />
        )}
      </div>
    );
  };

  const statusLine = (q: Quote) => (
    <div className="flex flex-wrap items-center gap-1">
      <StatusBadge
        label={QUOTE_STATUS_LABELS[q.status] ?? q.status}
        color={STATUS_COLORS[q.status]}
      />
      {q.fileReceivedAt && q.totalAmount === null && (
        <span className="text-xs text-amber-700">File in · figures to enter</span>
      )}
    </div>
  );

  if (shown.length === 0 && closed.length === 0) {
    return (
      <p className="text-sm text-gray-500 py-6 text-center">No quotes yet. Ask vendors for one.</p>
    );
  }

  return (
    <div className="space-y-4">
      {shown.length > 0 && (
        <>
          {/* Desktop: the matrix */}
          <div className="hidden sm:block overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left font-medium text-gray-500 py-2 pr-4">Item</th>
                  {shown.map((q) => (
                    <th
                      key={q.id}
                      scope="col"
                      className="text-right font-medium text-gray-900 py-2 px-3 align-bottom"
                    >
                      <div>{q.vendorName}</div>
                      <div className="flex justify-end mt-1">{statusLine(q)}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {matrix.rows.map((row) => (
                  <tr key={row.key} className="border-b border-gray-100">
                    <th scope="row" className="text-left font-normal text-gray-700 py-2 pr-4">
                      {row.extra && <span className="text-xs text-gray-400 mr-1">Extra</span>}
                      {row.quantity !== null && (
                        <span className="text-gray-400">{row.quantity} × </span>
                      )}
                      {row.label}
                    </th>
                    {shown.map((q) => (
                      <td key={q.id} className="text-right py-2 px-3">
                        <Amount cell={row.cells[q.id]} />
                      </td>
                    ))}
                  </tr>
                ))}
                <tr className="border-b border-gray-200">
                  <th scope="row" className="text-left font-semibold text-gray-900 py-2 pr-4">
                    Total
                  </th>
                  {shown.map((q) => (
                    <td key={q.id} className="text-right py-2 px-3">
                      <Amount cell={matrix.totals[q.id]} />
                      {matrix.lowestQuoteId === q.id && (
                        <Check size={14} className="inline ml-1 text-green-700" aria-hidden />
                      )}
                    </td>
                  ))}
                </tr>
                {budget !== null && (
                  <tr className="border-b border-gray-100">
                    <th scope="row" className="text-left font-normal text-gray-500 py-2 pr-4">
                      vs budget {money(budget)}
                    </th>
                    {shown.map((q) => (
                      <td
                        key={q.id}
                        className={`text-right py-2 px-3 ${(matrix.vsBudget[q.id] ?? 0) > 0 ? 'text-red-700' : 'text-gray-700'}`}
                      >
                        {signed(matrix.vsBudget[q.id])}
                      </td>
                    ))}
                  </tr>
                )}
                <tr className="border-b border-gray-100">
                  <th scope="row" className="text-left font-normal text-gray-500 py-2 pr-4">
                    Deposit
                  </th>
                  {shown.map((q) => (
                    <td key={q.id} className="text-right py-2 px-3 text-gray-700">
                      {describeDepositTerms(q.depositTerms, money) || '—'}
                    </td>
                  ))}
                </tr>
                <tr className="border-b border-gray-100">
                  <th scope="row" className="text-left font-normal text-gray-500 py-2 pr-4">
                    Valid until
                  </th>
                  {shown.map((q) => (
                    <td
                      key={q.id}
                      className={`text-right py-2 px-3 ${expired(q) ? 'text-red-700' : 'text-gray-700'}`}
                    >
                      {q.validUntil ? shortDay(q.validUntil) : '—'}
                      {expired(q) && (
                        <AlertTriangle size={14} className="inline ml-1" aria-label="Lapsed" />
                      )}
                    </td>
                  ))}
                </tr>
                <tr>
                  <th scope="row" className="text-left font-normal text-gray-500 py-2 pr-4">
                    Files
                  </th>
                  {shown.map((q) => (
                    <td key={q.id} className="text-right py-2 px-3 text-gray-700">
                      {q.documents.length > 0 ? (
                        <span className="inline-flex items-center gap-1">
                          <Paperclip size={14} aria-hidden /> {q.documents.length}
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                  ))}
                </tr>
                {!readOnly && (
                  <tr>
                    <td />
                    {shown.map((q) => (
                      <td key={q.id} className="py-2 px-3">
                        {actions(q)}
                      </td>
                    ))}
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Phone: one vendor at a time */}
          <div className="sm:hidden space-y-3">
            {lowest && (
              <p className="text-sm text-green-700 font-medium">
                Lowest: {lowest.vendorName} {money(lowest.totalAmount ?? 0)}
              </p>
            )}
            {shown.length > 1 && (
              <SegmentedControl
                ariaLabel="Quote"
                variant="chips"
                options={shown.map((q) => ({ id: q.id, label: q.vendorName }))}
                value={phoneQuote.id}
                onChange={setPhoneQuoteId}
              />
            )}
            <div
              className="rounded-xl border border-gray-200 p-4 space-y-3"
              data-testid="quote-card"
            >
              <div className="flex items-start justify-between gap-2">
                <h4 className="font-semibold text-gray-900">{phoneQuote.vendorName}</h4>
                {statusLine(phoneQuote)}
              </div>
              <dl className="space-y-1 text-sm">
                {matrix.rows
                  .filter((row) => !row.extra || row.cells[phoneQuote.id]?.amount !== null)
                  .map((row) => (
                    <div key={row.key} className="flex justify-between gap-3">
                      <dt className="text-gray-600">
                        {row.extra && <span className="text-xs text-gray-400 mr-1">Extra</span>}
                        {row.label}
                      </dt>
                      <dd>
                        <Amount cell={row.cells[phoneQuote.id]} />
                      </dd>
                    </div>
                  ))}
                <div className="flex justify-between gap-3 border-t border-gray-100 pt-1 font-semibold">
                  <dt>Total</dt>
                  <dd>
                    <Amount cell={matrix.totals[phoneQuote.id]} />
                  </dd>
                </div>
                {budget !== null && (
                  <div className="flex justify-between gap-3 text-gray-600">
                    <dt>vs budget</dt>
                    <dd>{signed(matrix.vsBudget[phoneQuote.id])}</dd>
                  </div>
                )}
                {phoneQuote.depositTerms && (
                  <div className="flex justify-between gap-3 text-gray-600">
                    <dt>Deposit</dt>
                    <dd>{describeDepositTerms(phoneQuote.depositTerms, money)}</dd>
                  </div>
                )}
                {phoneQuote.validUntil && (
                  <div
                    className={`flex justify-between gap-3 ${expired(phoneQuote) ? 'text-red-700' : 'text-gray-600'}`}
                  >
                    <dt>Valid until</dt>
                    <dd>{shortDay(phoneQuote.validUntil)}</dd>
                  </div>
                )}
              </dl>
              {actions(phoneQuote, true)}
            </div>
          </div>
        </>
      )}

      {quotes.some((q) => q.revisions.length > 0) && (
        <details className="text-sm">
          <summary className="cursor-pointer min-h-11 sm:min-h-0 text-gray-600">
            Earlier versions
          </summary>
          <ul className="mt-2 space-y-1">
            {quotes.flatMap((q) =>
              q.revisions.map((r) => (
                <li key={r.id} className="text-gray-600">
                  {q.vendorName}: {r.totalAmount !== null ? money(r.totalAmount) : 'no total'} —
                  quoted{' '}
                  {new Date(r.quotedAt).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                  })}
                </li>
              )),
            )}
          </ul>
        </details>
      )}

      {closed.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-500 mb-1">Declined or withdrawn</h4>
          <ul className="text-sm space-y-1">
            {closed.map((q) => (
              <li key={q.id} className="flex justify-between gap-3 text-gray-500">
                <span>
                  {q.vendorName} · {QUOTE_STATUS_LABELS[q.status] ?? q.status}
                </span>
                <span>{q.totalAmount !== null ? money(q.totalAmount) : '—'}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
